/**
 * selectedProductPerRoom.ts
 *
 * Per-app-session memory of the product the user has selected in each
 * room/conversation. Lets the composer's "PRODUCT SELECTED" quote-bubble
 * survive a room switch and reappear when the user returns to the same
 * room.
 *
 * Scope:
 *   - In-memory only (resets on hard refresh).
 *   - Keyed by `${shopId}:${buyerId}` so two rooms in the same shop with
 *     different buyers don't collide. Suitable for the chat composer's
 *     existing prop set; no need to thread roomId.
 *   - Caller deletes via `clear()` when the user explicitly dismisses
 *     the bubble or the product has been sent.
 */

import type { Product } from "src/app/modules/product/types/ProductListing"

const store = new Map<string, Product>()

export const roomKey = (
  shopId: string | undefined,
  buyerId: string | undefined
): string | null => {
  if (!shopId || !buyerId) return null
  return `${shopId}:${buyerId}`
}

export const get = (key: string | null): Product | null => {
  if (!key) return null
  return store.get(key) ?? null
}

export const set = (key: string | null, product: Product): void => {
  if (!key) return
  store.set(key, product)
}

export const clear = (key: string | null): void => {
  if (!key) return
  store.delete(key)
}

export const clearAll = (): void => {
  store.clear()
}

const selectedProductPerRoom = { roomKey, get, set, clear, clearAll }
export default selectedProductPerRoom
